import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, Link } from 'react-router';
import { DISCLAIMERS } from '@erectus/shared';
import { Button, Card, ErrorState, Field, Spinner, TextInput } from '@/ui/primitives.js';
import { useAuth } from '@/stores/auth.js';
import { isNative, platform } from '@/lib/device.js';

/** Web Sign in with Apple also needs a Services ID and key in the provider config. */
const appleOnWeb = import.meta.env.VITE_APPLE_SIGNIN_ENABLED === '1';

export function LoginPage(): ReactNode {
  const { user, loading, error, signInGoogle, signInApple, sendEmailLink, completeEmailLink } =
    useAuth();
  const [email, setEmail] = useState('');
  const [linkSent, setLinkSent] = useState(false);
  const [busy, setBusy] = useState(false);

  // Returning from an email sign-in link.
  useEffect(() => {
    void completeEmailLink();
  }, [completeEmailLink]);

  if (loading) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <Spinner label="Loading" />
      </div>
    );
  }
  if (user) return <Navigate to="/progress" replace />;

  const onEmail = async (): Promise<void> => {
    setBusy(true);
    await sendEmailLink(email.trim());
    setBusy(false);
    setLinkSent(true);
  };

  return (
    <div
      // One screen tall and scrolls inside itself (the document never scrolls);
      // my-auto centres the content without clipping it on a short screen.
      className="mx-auto flex h-dvh w-full max-w-screen-sm flex-col overflow-y-auto px-6"
      style={{
        paddingTop: 'calc(1.5rem + env(safe-area-inset-top))',
        paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))',
      }}
    >
      <div className="my-auto flex flex-col gap-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Erectus</h1>
          <p className="mt-1 text-sm text-ink-soft">
            Put your phone at your side while you work. It measures how your sitting posture holds
            up over the hour — on your device.
          </p>
        </div>

        {error ? <ErrorState title="Sign-in problem" message={error} /> : null}

        <Card className="grid gap-3">
          <Button onClick={() => void signInGoogle()}>Continue with Google</Button>
          {/*
            Sign in with Apple. On iOS it is native and needs only the provider
            enabled in Firebase Auth (done 2026-10-06), so it always shows there —
            App Store guideline 4.8 requires it beside Google. The web flow also
            needs a Services ID (com.uihyun.posture.signin, 2026-10-15) whose
            return URLs list every host's /__/auth/handler, hence the flag.
          */}
          {platform() === 'ios' ||
          (appleOnWeb && !isNative() && /iphone|ipad|mac/i.test(navigator.userAgent)) ? (
            <Button variant="secondary" onClick={() => void signInApple()}>
              Continue with Apple
            </Button>
          ) : null}

          <div className="my-1 flex items-center gap-3 text-xs text-muted">
            <span className="h-px flex-1 bg-line" />
            or
            <span className="h-px flex-1 bg-line" />
          </div>

          {linkSent ? (
            <p className="text-sm text-ink-soft">
              Check <strong>{email}</strong> for a sign-in link.
            </p>
          ) : (
            <>
              <Field label="Email sign-in link" hint="No password. We email you a one-time link.">
                <TextInput
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Field>
              <Button
                variant="secondary"
                disabled={busy || !email.includes('@')}
                onClick={() => void onEmail()}
              >
                {busy ? 'Sending…' : 'Email me a link'}
              </Button>
            </>
          )}
        </Card>

        <div className="space-y-2 text-xs text-muted">
          <p>{DISCLAIMERS.long}</p>
          <p className="flex flex-wrap gap-x-3 gap-y-1">
            <Link className="underline" to="/legal/privacy-policy">
              Privacy Policy
            </Link>
            <Link className="underline" to="/legal/consumer-health-data">
              Consumer Health Data
            </Link>
            <Link className="underline" to="/legal/terms">
              Terms
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
